import { GroupChannel, SuperChannelFilter } from '@sendbird/chat/groupChannel'
import { MentionType, UserMessage } from '@sendbird/chat/message'

/**
 * Discussion list/unread queries use createMyGroupChannelListQuery, which is
 * membership-scoped. Super Group create is decided by the backend (Gentiva only).
 * Always include Super Groups so super admins (no org on loginInfo) still see
 * threads they created or joined. Orgs with no Super Groups get the same results
 * as NON_SUPER.
 */
export const getSendbirdSuperChannelFilter = (): SuperChannelFilter => SuperChannelFilter.ALL

/**
 * InfiniteScroll stops asking for pages when a fetch does not increase dataLength.
 * Super Group pages can become empty after read-state filtering even while Sendbird
 * still has more pages, so advance past those gaps before returning to the caller.
 */
export const fetchUntilPageHasItemsOrQueryEnds = async <T>(
  fetchPage: () => Promise<T[]>,
  hasNextPage: () => boolean,
): Promise<T[]> => {
  const page = await fetchPage()
  if (page.length || !hasNextPage()) {
    return page
  }
  return fetchUntilPageHasItemsOrQueryEnds(fetchPage, hasNextPage)
}

const lastReadStorageKey = (userId: string) => `gcc_discussion_last_read:${userId}`
const LAST_READ_MAX_ENTRIES = 200

type LastReadMap = Record<string, number>

let lastReadCache: { userId: string; map: LastReadMap } | null = null

const pruneLastReadMap = (map: LastReadMap): LastReadMap => {
  const entries = Object.entries(map)
  if (entries.length <= LAST_READ_MAX_ENTRIES) {
    return map
  }
  return Object.fromEntries(entries.sort((a, b) => b[1] - a[1]).slice(0, LAST_READ_MAX_ENTRIES))
}

const readLocalLastReadMap = (userId: string): LastReadMap => {
  if (!userId || typeof localStorage === 'undefined') {
    return {}
  }
  if (lastReadCache?.userId === userId) {
    return lastReadCache.map
  }
  try {
    const raw = localStorage.getItem(lastReadStorageKey(userId))
    const map = raw ? (JSON.parse(raw) as LastReadMap) : {}
    lastReadCache = { userId, map }
    return map
  } catch {
    return {}
  }
}

/**
 * Super Groups may not advance myLastRead when markAsRead() runs (read receipts are
 * unsupported). Persist a local cursor so opened threads do not stay permanently unread.
 * Use Sendbird message time only — Date.now() can mark threads read forever if the
 * workstation clock is ahead of the server.
 */
export const recordDiscussionLastRead = (channel: GroupChannel, currentUserId: string): void => {
  if (!currentUserId || !channel?.url) {
    return
  }
  const lastMessageAt = (channel.lastMessage as UserMessage | null)?.createdAt || 0
  const timestamp = Math.max(lastMessageAt, channel.myLastRead || 0)
  if (!timestamp) {
    return
  }
  const existing = readLocalLastReadMap(currentUserId)
  const map = pruneLastReadMap({
    ...existing,
    [channel.url]: Math.max(existing[channel.url] || 0, timestamp),
  })
  lastReadCache = { userId: currentUserId, map }
  try {
    localStorage.setItem(lastReadStorageKey(currentUserId), JSON.stringify(map))
  } catch {
    // ignore quota / private mode
  }
}

export const getEffectiveLastRead = (channel: GroupChannel, currentUserId: string): number => {
  const local = currentUserId ? readLocalLastReadMap(currentUserId)[channel.url] || 0 : 0
  return Math.max(channel.myLastRead || 0, local)
}

const asUserMessage = (channel: GroupChannel): UserMessage | null =>
  (channel.lastMessage as UserMessage | null) ?? null

const lastMessageMentionsUser = (channel: GroupChannel, currentUserId: string): boolean => {
  if (!currentUserId) {
    return false
  }
  const lastMessage = asUserMessage(channel)
  if (!lastMessage) {
    return false
  }
  if (lastMessage.mentionType === MentionType.CHANNEL) {
    return true
  }
  return lastMessage.mentionedUserIds?.includes(currentUserId) ?? false
}

const lastMessageIsFromSomeoneElse = (channel: GroupChannel, currentUserId: string): boolean => {
  if (!currentUserId) {
    return false
  }
  const lastMessage = asUserMessage(channel)
  const senderId = lastMessage?.sender?.userId
  if (!lastMessage?.createdAt || !senderId) {
    return false
  }
  return senderId !== currentUserId
}

const isSuperGroupLastMessageUnread = (channel: GroupChannel, currentUserId: string): boolean => {
  if (!channel.isSuper) {
    return false
  }
  if (!lastMessageIsFromSomeoneElse(channel, currentUserId)) {
    return false
  }
  const lastMessage = asUserMessage(channel)
  if (!lastMessage) {
    return false
  }
  return lastMessage.createdAt > getEffectiveLastRead(channel, currentUserId)
}

/**
 * Super Groups often leave unreadMessageCount at 0, so UnreadChannelFilter.UNREAD_MESSAGE
 * hides them unless the Discussions page is open and a live handler prepends the channel.
 * Last-message-after-last-read is Super Group only so private groups stay on SDK counts.
 */
export const isDiscussionUnread = (channel: GroupChannel, currentUserId: string): boolean => {
  if ((channel.unreadMessageCount ?? 0) > 0 || (channel.unreadMentionCount ?? 0) > 0) {
    return true
  }
  return isSuperGroupLastMessageUnread(channel, currentUserId)
}

export const getDiscussionUnreadMentionCount = (
  channel: GroupChannel,
  currentUserId: string,
): number => {
  if ((channel.unreadMentionCount ?? 0) > 0) {
    return channel.unreadMentionCount
  }
  const lastMessage = asUserMessage(channel)
  if (
    channel.isSuper &&
    lastMessageMentionsUser(channel, currentUserId) &&
    lastMessage &&
    lastMessage.createdAt > getEffectiveLastRead(channel, currentUserId) &&
    lastMessageIsFromSomeoneElse(channel, currentUserId)
  ) {
    return 1
  }
  return 0
}

export const getDiscussionUnreadDisplayCount = (
  channel: GroupChannel,
  currentUserId: string,
): number => {
  if ((channel.unreadMessageCount ?? 0) > 0) {
    return channel.unreadMessageCount
  }
  const mentionCount = getDiscussionUnreadMentionCount(channel, currentUserId)
  if (mentionCount > 0) {
    return mentionCount
  }
  return isDiscussionUnread(channel, currentUserId) ? 1 : 0
}

const channelRecency = (channel: GroupChannel): number =>
  channel.lastMessage?.createdAt ?? channel.createdAt ?? 0

/**
 * Unread mentions first, then most recent last message. Keeps @tags from sinking
 * under a long Super Group unread list.
 */
export const sortChannelsMentionsFirst = (
  channels: (GroupChannel | null)[],
  currentUserId: string,
): (GroupChannel | null)[] =>
  [...channels].sort((left, right) => {
    if (!left && !right) {
      return 0
    }
    if (!left) {
      return 1
    }
    if (!right) {
      return -1
    }
    const leftMention = getDiscussionUnreadMentionCount(left, currentUserId) > 0 ? 1 : 0
    const rightMention = getDiscussionUnreadMentionCount(right, currentUserId) > 0 ? 1 : 0
    if (leftMention !== rightMention) {
      return rightMention - leftMention
    }
    return channelRecency(right) - channelRecency(left)
  })

/** Super Groups whose SDK unread counts are 0 but the last-read fallback says unread. */
export const getSuperGroupFallbackUnreadCount = (
  channels: GroupChannel[],
  currentUserId: string,
): number =>
  channels
    .filter(
      channel =>
        channel.isSuper &&
        (channel.unreadMessageCount ?? 0) === 0 &&
        (channel.unreadMentionCount ?? 0) === 0 &&
        isDiscussionUnread(channel, currentUserId),
    )
    .reduce((sum, channel) => sum + getDiscussionUnreadDisplayCount(channel, currentUserId), 0)
